"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, Gauge, Network } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const applicationsData = [{
  id: "app-1",
  title: "Continuous Inline Manufacturing Lines",
  tagline: "High-throughput inline verification",
  description: "Direct integration into moving powder, liquid, or granulation lines to monitor product composition and uniformity without sample extraction.",
  icon: Activity,
  points: ["Real-time spectral feedback across active feed lines", "Immediate detection of blend transitions and drift", "Compatible with high-speed automated transport systems"],
  link: "/ProcessAnalyticalTechnologyPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/inline_manufacturing_line.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/inline_manufacturing_line.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/inline_manufacturing_line.png-wvc-sizes",
    alt: "Continuous inline manufacturing line analytical interface"
  }
}, {
  id: "app-2",
  title: "Direct-Connected Process Installations",
  tagline: "Vessel and reactor integration",
  description: "Flange, sanitary port, and immersion probe connections mounted directly to reactors, dryers, and mixing vessels under full process pressure.",
  icon: Gauge,
  points: ["Standard sanitary and industrial flange mountings", "Continuous trending through critical reaction stages", "Ruggedized optical interfaces for harsh environments"],
  link: "/ProcessGuardianPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_installation_probe.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_installation_probe.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_installation_probe.png-wvc-sizes",
    alt: "Direct-connected process vessel sensor setup"
  }
}, {
  id: "app-3",
  title: "Automated Stream Quality Verification",
  tagline: "Multi-point routine quality checking",
  description: "Multiplexed sampling systems cycling across multiple transfer points to provide continuous automated stream clearance and batch verification.",
  icon: Network,
  points: ["Multiplexed channel support for multi-line facilities", "Standardized data output to DCS and SCADA networks", "Automated drift correction and calibration checks"],
  link: "/MatrixFIIPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/stream_quality_verification.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/stream_quality_verification.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/stream_quality_verification.webp-wvc-sizes",
    alt: "Automated stream quality verification instrument rack"
  }
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="applicationsfeatures" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              <Cpu className="size-3.5 text-primary mr-1" />
              Process Analytical Deployments
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Standard Application Architectures
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Proven instrumentation setups engineered for regulated pharmaceutical, chemical, and industrial processing lines across Ireland and the UK.
          </p>
        </div>

        {/* Applications Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {applicationsData.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
                <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 bg-card overflow-hidden">
                  <div>
                    {/* Visual Asset Container */}
                    <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                      <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs p-2 rounded-md shadow-xs border border-border">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                    </div>

                    <CardHeader className="pt-6">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        {item.tagline}
                      </p>
                      <CardTitle className="text-xl font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm mt-2 leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <ul className="space-y-2.5">
                        {item.points.map((point, pointIdx) => <li key={pointIdx} data-index={pointIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-6 border-t border-border mt-6">
                    <Button asChild variant="outline" className="w-full justify-between group hover:border-primary">
                      <Link to={item.link}>
                        <span>Explore Instrumentation</span>
                        <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Consultation Link */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Have specific process geometry or sampling requirements?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Our application specialists assist with optical probe selection, flange design, and installation review.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0">
            <Link to="/DiscussYourApplicationPage">
              Discuss Your Application
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}